/**
 * Year-End Tax Planning — Client Detail Page
 *
 * Opens when a client row is clicked on the tax-planning page. For each
 * generated action item (Super, Prepaid Expenses, Asset Write-off, Div 7A,
 * Trust Distribution, etc.), this page shows:
 *   - Step-by-step how-to
 *   - Documents to gather
 *   - Verified ATO citations with links
 *   - Related RoniDoc tools
 *   - Deadline countdown
 *   - Export to Word
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar, ArrowLeft, AlertTriangle, CheckCircle2, Clock, BookOpen, ExternalLink,
  Download, FileText, Calculator, Shield, TrendingUp, Briefcase, Users, Car, Heart,
  Building2, DollarSign, Package, Zap, Scale, ClipboardList, Target,
} from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

const PRIORITY_COLORS: Record<string, string> = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#22c55e' };
const PRIORITY_STYLES: Record<string, string> = {
  critical: 'bg-red-500/10 border-l-red-500',
  high: 'bg-amber-500/5 border-l-amber-500',
  medium: 'bg-blue-500/5 border-l-blue-500',
  low: 'bg-green-500/5 border-l-green-500',
};

// ==================== Category meta: how-to, docs, ATO refs ====================

interface AtoRef { title: string; url: string; }
interface CategoryMeta {
  icon: any;
  howTo: string[];
  docsToGather: string[];
  atoRefs: AtoRef[];
  internalTools?: { title: string; href: string }[];
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  'Trust Distribution': {
    icon: Users,
    howTo: [
      'Review trust deed to confirm beneficiary classes and the trustee\'s power to stream income and capital gains.',
      'Obtain estimated year-end trust income broken down by character (franked dividends, capital gains, foreign income, ordinary income).',
      'Model distributions across beneficiaries considering marginal rates, CGT discount eligibility, and company tax rates.',
      'Consider Section 100A risk — distributions must be made for genuine benefit of the beneficiary, not a reimbursement agreement.',
      'Draft the Trustee Resolution with specific dollar amounts or percentages — must be signed and dated before 30 June.',
      'File the signed resolution with the trust records. ATO may request sight of it during audit.',
      'Advise beneficiaries of the distribution so they can plan for the tax liability.',
    ],
    docsToGather: [
      'Current trust deed (including any amendments)',
      'Year-to-date trust profit and loss',
      'Beneficiary list with income status and existing income (to estimate marginal rates)',
      'Capital gains schedule (if streaming CGT)',
      'Prior year trustee resolution (as reference)',
    ],
    atoRefs: [
      { title: 'Trustee resolutions — ATO checklist', url: 'https://www.ato.gov.au/businesses-and-organisations/trusts/trust-income-losses-and-capital-gains/resolutions-checklist' },
      { title: 'Trust reimbursement agreement — s100A', url: 'https://www.ato.gov.au/businesses-and-organisations/trusts/trust-income-losses-and-capital-gains/trust-taxation-reimbursement-agreement' },
      { title: 'PCG 2022/2 — Section 100A compliance approach', url: 'https://www.ato.gov.au/law/view/pdf?DocId=COG/PCG20222/NAT/ATO/00001' },
      { title: 'Tax issues for trusts — tips and traps', url: 'https://www.ato.gov.au/businesses-and-organisations/trusts/trusts-tax-risks-and-compliance/tax-issues-for-trusts-tips-and-traps' },
    ],
    internalTools: [
      { title: 'Trust Distribution Planner', href: '/panel/compliance/trust-distribution' },
    ],
  },
  'Superannuation': {
    icon: TrendingUp,
    howTo: [
      'Check the current financial year concessional contributions (SG + salary sacrifice + personal deductible).',
      'Calculate remaining headroom under the $30,000 concessional cap.',
      'If total super balance (TSB) was under $500K at 30 June of prior year, check Unused Carry-Forward amounts in ATO online services.',
      'Confirm whether Div 293 tax applies — triggered if income + concessional contributions exceed $250K.',
      'If making a personal deductible contribution, submit a valid Notice of Intent to Claim (s290-170) to the super fund before lodging tax return OR before the fund is closed/rolled over.',
      'Transfer the contribution with enough lead time — must be RECEIVED and ALLOCATED by the fund before 30 June. Allow 3–5 business days.',
      'Retain fund acknowledgement of the Notice of Intent.',
    ],
    docsToGather: [
      'Superannuation member statement showing YTD contributions by category',
      'Total Super Balance at 30 June of prior year',
      'YTD income summary (to estimate Div 293 exposure)',
      'Payslips showing SG + salary sacrifice amounts',
      'Bank transfer confirmation (contribution evidence)',
      'Notice of Intent to Claim form (if personal deductible contribution)',
    ],
    atoRefs: [
      { title: 'Concessional contributions cap — carry-forward', url: 'https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/concessional-contributions-cap' },
      { title: 'Key superannuation rates and thresholds', url: 'https://www.ato.gov.au/tax-rates-and-codes/key-superannuation-rates-and-thresholds/contributions-caps' },
      { title: 'Division 293 tax — high income earners', url: 'https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/division-293-tax-on-concessional-contributions-by-high-income-earners' },
      { title: 'Personal superannuation contributions deduction', url: 'https://www.ato.gov.au/individuals-and-families/your-tax-return/instructions-to-complete-your-tax-return/mytax-instructions/2025/deductions/other-deductions/personal-super-contributions' },
    ],
  },
  'Prepaid Expenses': {
    icon: FileText,
    howTo: [
      'Confirm Small Business Entity status — aggregated turnover below $10M in the current or prior year.',
      'Identify eligible expenses: rent, insurance, professional subscriptions, memberships, software, interest on loans.',
      'Check each expense has a service period of 12 months or less and ends before the next 30 June.',
      'Check the payment is for a SERVICE (not stock or capital asset — those don\'t qualify).',
      'Pay and obtain a paid tax invoice before 30 June.',
      'Record the prepayment in Xero/MYOB against the correct expense code (not as a Prepaid Expenses asset if claiming immediate deduction).',
      'Keep supporting documentation — supplier agreement showing the service period.',
    ],
    docsToGather: [
      'Aggregated turnover calculation (to confirm SBE)',
      'Current lease, insurance policies, subscription agreements',
      'Supplier tax invoices showing service period',
      'Payment confirmations (bank statements / receipts)',
    ],
    atoRefs: [
      { title: 'Deductions for prepaid expenses — 2025', url: 'https://www.ato.gov.au/forms-and-instructions/deductions-for-prepaid-expenses-2024' },
      { title: 'Small business concessions — overview', url: 'https://www.ato.gov.au/businesses-and-organisations/small-business-newsroom' },
    ],
  },
  'Asset Write-off': {
    icon: Package,
    howTo: [
      'Confirm Small Business Entity status (turnover under $10M).',
      'Identify assets to purchase: computers, tools, office equipment, machinery, vehicles (subject to car limit).',
      'Ensure each individual asset is under the $20,000 threshold (GST-exclusive for GST-registered businesses).',
      'Purchase and have the asset DELIVERED and INSTALLED READY FOR USE before 30 June — mere ordering is not enough.',
      'For vehicles, the car cost limit for depreciation is $69,674 (2025–26) — amounts above are not deductible.',
      'Claim the full cost in the current year\'s tax return.',
      'If an asset exceeds $20,000, allocate to the small business pool (15% first year, 30% thereafter).',
    ],
    docsToGather: [
      'Tax invoice showing GST-exclusive price',
      'Delivery / installation confirmation dated before 30 June',
      'Photographs or asset register entry (helpful in audit)',
      'Finance agreement if financed (deduction still allowed)',
    ],
    atoRefs: [
      { title: 'Instant asset write-off — eligibility and operation', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business/instant-asset-write-off' },
      { title: '$20,000 instant asset write-off for 2025–26', url: 'https://www.ato.gov.au/businesses-and-organisations/small-business-newsroom/20000-instant-asset-write-off-for-2025-26' },
      { title: 'Simpler depreciation rules for small business', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business' },
      { title: 'Assets and exclusions', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business/assets-and-exclusions' },
    ],
  },
  'Director Bonus': {
    icon: Briefcase,
    howTo: [
      'Compare company tax rate (25%) with director\'s marginal rate to confirm tax benefit.',
      'Draft a board resolution approving the bonus — must be signed and dated before 30 June.',
      'Process the bonus through payroll: PAYG withholding + SG of 12% applies.',
      'Pay SG to the fund by 28 July to preserve deductibility.',
      'Record the bonus as an employment expense in the company\'s financial year.',
      'If bonus is only accrued (not paid), ensure it\'s a present obligation — otherwise ATO may deny the deduction.',
      'Update the director\'s ETP/PAYG summary and lodge STP finalisation.',
    ],
    docsToGather: [
      'Board minutes / resolution approving the bonus',
      'Payslip reflecting the bonus',
      'SG payment confirmation',
      'Updated STP finalisation at year end',
    ],
    atoRefs: [
      { title: 'Employment termination payments and bonuses', url: 'https://www.ato.gov.au/businesses-and-organisations/corporate-tax-measures-and-assurance' },
      { title: 'Super guarantee — how much to pay', url: 'https://www.ato.gov.au/businesses-and-organisations/super-for-employers/paying-super-contributions/how-much-super-to-pay' },
      { title: 'PAYG withholding — payments to employees', url: 'https://www.ato.gov.au/businesses-and-organisations/pay-as-you-go-withholding' },
    ],
  },
  'Bad Debts': {
    icon: AlertTriangle,
    howTo: [
      'Produce an aged receivables report at 30 June.',
      'For each doubtful debt, evaluate: Is the amount genuinely unrecoverable? Has the debtor become insolvent, disappeared, or disputed in bad faith?',
      'Confirm the amount was previously included in assessable income (cash basis taxpayers rarely qualify unless invoiced income was already recognised).',
      'Take a formal written resolution (board minute or file note) WRITING OFF the debt — must occur before 30 June.',
      'Reverse the debt in Xero/MYOB against Bad Debts Expense, creating a GST decreasing adjustment.',
      'Retain all correspondence showing collection attempts (emails, letters of demand, lawyer\'s letters).',
      'If debt is later recovered, include the recovery as assessable income.',
    ],
    docsToGather: [
      'Aged debtors report',
      'Collection correspondence',
      'Insolvency notice (if debtor is in liquidation / bankruptcy)',
      'Written resolution or board minute',
      'Updated customer ledger showing write-off',
    ],
    atoRefs: [
      { title: 'Bad debts — writing off for tax purposes (ATO)', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business' },
      { title: 'GST adjustments for bad debts', url: 'https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst' },
    ],
  },
  'Trading Stock': {
    icon: Package,
    howTo: [
      'Conduct a physical stocktake at 30 June.',
      'Choose valuation method for each item: Cost, Market Selling Value, or Replacement Value. Different items may use different methods.',
      'Identify obsolete, damaged, or slow-moving stock. Consider writing down to market selling value (can be $0 for unsellable stock).',
      'Document the stocktake — count sheets, photographs of obsolete items, market price evidence.',
      'Adjust Xero/MYOB stock holding to reflect the valuation.',
      'Check for stock taken for private use (trade items used personally) — add back at cost.',
    ],
    docsToGather: [
      'Stocktake worksheets and tally sheets',
      'Photographic evidence for obsolete / damaged stock',
      'Market price evidence (competitor prices, eBay listings)',
      'Purchase invoices (for Cost method support)',
    ],
    atoRefs: [
      { title: 'Trading stock — general valuation rules', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/in-detail/business-deductions' },
      { title: 'Simplified trading stock rules (SBE)', url: 'https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances/simpler-depreciation-for-small-business' },
    ],
  },
  'Motor Vehicle': {
    icon: Car,
    howTo: [
      'Check whether the current logbook is valid — a 12-week representative period, not older than 5 years.',
      'If logbook has expired, start a new 12-week period immediately (does not need to cover 30 June itself).',
      'Record every trip in the 12-week period: date, start and end odometer, purpose, business vs private.',
      'At year end, calculate the business use percentage from the logbook.',
      'Apply that percentage to total running costs (fuel, rego, insurance, repairs, depreciation).',
      'For vehicles purchased this year, note the car cost limit of $69,674 for depreciation purposes.',
      'Alternative: Cents-per-km method — up to 5,000 km at 88c/km (2025–26). No logbook needed but lower claim.',
    ],
    docsToGather: [
      'Current logbook (or screenshot from an app like Driversnote)',
      'Fuel, rego, insurance receipts',
      'Odometer photos at 30 June',
      'Service / repair invoices',
    ],
    atoRefs: [
      { title: 'Car expenses — logbook method', url: 'https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-expenses/car-expenses' },
      { title: 'Car cost limit for depreciation', url: 'https://www.ato.gov.au/tax-rates-and-codes/car-cost-limit-for-depreciation' },
    ],
  },
  'Donations': {
    icon: Heart,
    howTo: [
      'Identify the charities you want to support.',
      'Verify each charity has DGR (Deductible Gift Recipient) status via the ABN Lookup or ACNC register.',
      'Make the donation before 30 June — bank transfer or card payment.',
      'Obtain a receipt from the DGR showing the DGR\'s name, ABN, amount, date, and a statement that it is a gift.',
      'Retain the receipt for 5 years from lodgement.',
      'If donating assets (shares, property), additional rules apply — seek advice first.',
    ],
    docsToGather: [
      'DGR tax-deductible receipts',
      'Bank statements showing payment',
      'DGR endorsement confirmation (ABN lookup result)',
    ],
    atoRefs: [
      { title: 'Gifts and donations — ATO', url: 'https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/gifts-and-donations' },
      { title: 'ABN Lookup — confirm DGR status', url: 'https://abr.business.gov.au/' },
      { title: 'ACNC Charity Register', url: 'https://www.acnc.gov.au/charity' },
    ],
  },
  'Income Timing': {
    icon: Clock,
    howTo: [
      'Confirm whether the business is on cash or accrual basis for tax.',
      'Cash basis: income is derived when received. Defer by delaying invoicing — but only works if the payment is also delayed.',
      'Accrual basis: income is derived when earned (goods delivered / services performed). Deferring invoices does NOT defer income if the work is done.',
      'Consider legitimate business reasons to delay billing — avoid arrangements that look artificial, which may trigger anti-avoidance (Part IVA).',
      'For contracts with milestones, discuss with the customer whether a milestone can shift.',
      'Balance this against cash flow — deferring income also defers cash receipts.',
    ],
    docsToGather: [
      'Outstanding quotes / work-in-progress schedule',
      'Contract terms showing milestone dates',
      'Email trail if customer requested delayed billing',
    ],
    atoRefs: [
      { title: 'Income derivation — cash vs accrual (TR 98/1)', url: 'https://www.ato.gov.au/law/view/document?DocID=TXR/TR981/NAT/ATO/00001' },
      { title: 'Part IVA general anti-avoidance', url: 'https://www.ato.gov.au/tax-and-super-professionals/for-tax-professionals/prevent-avoid-and-resolve-disputes/part-iva-general-anti-avoidance-rule-for-income-tax' },
    ],
  },
  'Medicare Levy Surcharge': {
    icon: Shield,
    howTo: [
      'Calculate estimated Income for MLS purposes — taxable income + reportable fringe benefits + reportable super + net investment losses.',
      'Check your family\'s income against the MLS thresholds.',
      'Compare the MLS cost vs cheapest Basic Hospital private health insurance premium.',
      'If insurance is cheaper, arrange cover to START BEFORE 30 June (or 1 July if you only need to avoid MLS next year — confirm with provider).',
      'Confirm the policy qualifies (must be Hospital Cover, not just Extras — Extras alone does not exempt).',
      'Retain Policy Statement as evidence.',
    ],
    docsToGather: [
      'Income for MLS purposes worksheet',
      'Private health insurance policy documents',
      'Premium payment receipt',
    ],
    atoRefs: [
      { title: 'Medicare Levy Surcharge — ATO', url: 'https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge' },
      { title: 'Income for MLS purposes', url: 'https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/income-for-medicare-levy-surcharge-purposes' },
      { title: 'privatehealth.gov.au — compare policies', url: 'https://www.privatehealth.gov.au/' },
    ],
  },
  'Division 7A': {
    icon: Scale,
    howTo: [
      'Extract all loans and payments from the company to shareholders, directors, or their associates.',
      'For each, identify whether a compliant s109N loan agreement is in place (written, signed, specifies interest and term).',
      'If no agreement: the loan becomes a deemed unfranked dividend at the end of the income year unless repaid before the company\'s lodgement day.',
      'Calculate minimum yearly repayment (MYR) for each loan using the Division 7A Calculator.',
      'Ensure MYR is paid by the company\'s lodgement day (typically 15 May following year for taxpayers with agents).',
      'Charge interest at the 2025–26 benchmark rate — confirm current rate on ATO website before finalising.',
      'If a shortfall exists, consider: refinancing, using frankable dividend, or a top-up payment before lodgement day.',
    ],
    docsToGather: [
      'Director / shareholder loan account transactions from Xero',
      'Current s109N loan agreements',
      'Prior year MYR calculations',
      'Bank statements evidencing repayments',
    ],
    atoRefs: [
      { title: 'Division 7A loans — ATO detailed guide', url: 'https://www.ato.gov.au/businesses-and-organisations/corporate-tax-measures-and-assurance/private-company-benefits-division-7a-dividends/in-detail/division-7a-loans' },
      { title: 'Division 7A benchmark interest rate', url: 'https://www.ato.gov.au/tax-rates-and-codes/division-7a-benchmark-interest-rate' },
      { title: 'Division 7A calculator and decision tool', url: 'https://www.ato.gov.au/calculators-and-tools/division-7a-calculator-and-decision-tool' },
      { title: 'Debunking Division 7A myths', url: 'https://www.ato.gov.au/businesses-and-organisations/corporate-tax-measures-and-assurance/private-company-benefits-division-7a-dividends/in-detail/division-7a-myths-debunked' },
    ],
    internalTools: [
      { title: 'Div 7A Checker', href: '/panel/compliance/div7a' },
    ],
  },
  'Super Guarantee': {
    icon: DollarSign,
    howTo: [
      'Extract YTD Ordinary Time Earnings (OTE) for each employee from the payroll system.',
      'Compare SG paid to SG owed (OTE × 12% for 2025–26).',
      'Check Q4 (April–June) SG liabilities — due by 28 July.',
      'Transfer payments to funds via SuperStream clearing house with sufficient lead time (allow 5–7 business days).',
      'Obtain fund acknowledgement for each contribution.',
      'If any SG is late, compute the Super Guarantee Charge (SGC) — shortfall + nominal interest + admin fee — and lodge SGC statement.',
      'Remember: SGC is NOT tax deductible. Always pay SG on time to preserve deductibility.',
    ],
    docsToGather: [
      'Payroll YTD reports by employee',
      'SuperStream clearing house payment confirmations',
      'Fund acknowledgements / allocation notices',
      'STP reports',
    ],
    atoRefs: [
      { title: 'How much super to pay — SG rates', url: 'https://www.ato.gov.au/businesses-and-organisations/super-for-employers/paying-super-contributions/how-much-super-to-pay' },
      { title: 'Missed and late SG payments (SGC)', url: 'https://www.ato.gov.au/businesses-and-organisations/super-for-employers/missed-and-late-super-guarantee-payments' },
      { title: 'SuperStream — paying super', url: 'https://www.ato.gov.au/businesses-and-organisations/super-for-employers/paying-super-contributions' },
    ],
  },
};

// Fallback meta for categories not explicitly listed
const DEFAULT_META: CategoryMeta = {
  icon: ClipboardList,
  howTo: [
    'Review the action description to understand the strategy.',
    'Gather current-year financial data from Xero or MYOB.',
    'Consult with the client to confirm eligibility and appetite.',
    'Document the decision in a file note or email.',
    'Action before the deadline shown.',
  ],
  docsToGather: [
    'Current year-to-date profit and loss',
    'Supporting documentation for the action',
    'Client authorisation / email agreement',
  ],
  atoRefs: [
    { title: 'ATO for businesses and organisations', url: 'https://www.ato.gov.au/businesses-and-organisations' },
  ],
};

// ==================== Component ====================

export default function TaxPlanningDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const { data: planData, isLoading } = useQuery({
    queryKey: ['tax-planning', clientId],
    queryFn: async () => { const r = await apiClient.get(`/tax-planning/${clientId}`); return r.data.data; },
    enabled: !!clientId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-sm text-muted-foreground">Generating year-end plan...</span>
      </div>
    );
  }

  if (!planData || !planData.plan) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/compliance/tax-planning')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Tax Planning
        </Button>
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <p>No tax plan available for this client.</p>
            <p className="text-xs mt-2">Check Xero connection and data for the current financial year.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, financials, plan, eofy } = planData;
  const daysRemaining = eofy?.daysRemaining ?? 0;
  const isPreEOFY = daysRemaining > 0;

  // Export — richer version including how-to steps + ATO refs
  const exportPlan = async () => {
    const children: any[] = [
      new Paragraph({ children: [new TextRun({ text: 'Year-End Tax Planning Report', bold: true, size: 36, color: '2563eb' })], heading: HeadingLevel.HEADING_1, spacing: { after: 100 } }),
      new Paragraph({ children: [new TextRun({ text: client.name, bold: true, size: 28 })], spacing: { after: 50 } }),
      new Paragraph({ children: [new TextRun({ text: `FY${financials.fy} · ${daysRemaining} days to 30 June · Generated ${new Date().toLocaleDateString('en-AU')}`, size: 20, color: '666666', italics: true })], spacing: { after: 300 } }),
    ];

    plan.items.forEach((item: any) => {
      const meta = CATEGORY_META[item.category] || DEFAULT_META;
      children.push(new Paragraph({ children: [new TextRun({ text: `[${item.priority.toUpperCase()}] ${item.category}: ${item.action}`, bold: true, size: 24 })], heading: HeadingLevel.HEADING_2, spacing: { before: 240, after: 100 } }));
      children.push(new Paragraph({ children: [new TextRun({ text: item.description, size: 22 })], spacing: { after: 80 } }));
      if (item.estimatedSaving > 0) children.push(new Paragraph({ children: [new TextRun({ text: `Estimated Saving: ${fmtCurrency(item.estimatedSaving)}`, bold: true, size: 22, color: '22c55e' })], spacing: { after: 50 } }));
      children.push(new Paragraph({ children: [new TextRun({ text: `Deadline: ${item.deadline}`, size: 20, color: 'ef4444' })], spacing: { after: 120 } }));

      children.push(new Paragraph({ children: [new TextRun({ text: 'How to action', bold: true, size: 22 })], spacing: { after: 60 } }));
      meta.howTo.forEach((s, i) => {
        children.push(new Paragraph({ children: [new TextRun({ text: `${i + 1}. ${s}`, size: 20 })], spacing: { after: 40 } }));
      });

      children.push(new Paragraph({ children: [new TextRun({ text: 'Documents to gather', bold: true, size: 22 })], spacing: { before: 80, after: 60 } }));
      meta.docsToGather.forEach(s => {
        children.push(new Paragraph({ children: [new TextRun({ text: `• ${s}`, size: 20 })], spacing: { after: 40 } }));
      });

      children.push(new Paragraph({ children: [new TextRun({ text: 'ATO references', bold: true, size: 22 })], spacing: { before: 80, after: 60 } }));
      meta.atoRefs.forEach(ref => {
        children.push(new Paragraph({ children: [new TextRun({ text: `• ${ref.title} — ${ref.url}`, size: 18, color: '666666' })], spacing: { after: 40 } }));
      });

      children.push(new Paragraph({ children: [new TextRun({ text: `ATO rule: ${item.atoRule}`, size: 18, color: '666666', italics: true })], spacing: { before: 80, after: 120 } }));
    });

    children.push(new Paragraph({ children: [new TextRun({ text: 'Generated by RoniDoc AI — Confidential', size: 16, color: '999999', italics: true })], alignment: AlignmentType.CENTER, spacing: { before: 400 } }));

    const doc = new Document({ sections: [{ properties: {}, children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${client.name.replace(/[^a-zA-Z0-9]/g, '_')}_Tax_Plan_Detailed_${new Date().toISOString().split('T')[0]}.docx`);
    toast.success('Detailed tax plan exported');
  };

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/panel/compliance/tax-planning')} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to Tax Planning
      </Button>

      <PageHeader
        icon={Calendar}
        title={`${client.name} — Tax Planning Detail`}
        description="Step-by-step actions, required documentation, and verified ATO citations"
        iconColor="bg-orange-500/10 text-orange-500"
        actions={<Button size="sm" variant="outline" className="gap-1" onClick={exportPlan}><Download className="h-3.5 w-3.5" />Export Word</Button>}
      />

      {/* Client Summary Banner */}
      <Card className="border-l-4 border-l-orange-500">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h2 className="text-lg font-semibold">{client.name}</h2>
                <Badge variant="outline" className="text-[10px] capitalize">{client.entityType}</Badge>
                {client.industry && <Badge variant="outline" className="text-[10px]">{client.industry}</Badge>}
                {client.xeroConnected ? (
                  <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">
                    <svg className="h-2.5 w-2.5 mr-1" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
                    Xero connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">No Xero — manual data only</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                FY{financials.fy || 'current'} · {isPreEOFY ? (<><span className={cn('font-semibold', daysRemaining <= 30 ? 'text-red-600' : daysRemaining <= 60 ? 'text-amber-600' : '')}>{daysRemaining} days</span> to 30 June</>) : 'Financial year ended'}
              </p>
            </div>
            <div className="grid grid-cols-4 gap-6 text-right">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Turnover</p>
                <p className="text-base font-semibold">{fmtCurrency(financials.turnover ?? financials.income ?? 0)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Expenses</p>
                <p className="text-base font-semibold">{fmtCurrency(financials.expenses || 0)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Profit</p>
                <p className={cn('text-base font-semibold', (financials.profit || 0) < 0 && 'text-red-600')}>{fmtCurrency(financials.profit || 0)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase">Est. Saving</p>
                <p className="text-base font-semibold text-green-600">{fmtCurrency(plan.totalEstimatedSaving || 0)}</p>
              </div>
            </div>
          </div>

          {/* Data source explanation */}
          <div className="mt-4 p-2.5 rounded-md bg-muted/30 border text-[11px] text-muted-foreground leading-relaxed flex items-start gap-2">
            <BookOpen className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <strong className="text-foreground">Data source: </strong>
              {client.xeroConnected ? (
                <>
                  These numbers come directly from <strong className="text-foreground">Xero's Profit and Loss report</strong> for FY{financials.fy || 'current'}.
                  Not from uploaded files, the knowledge base, or manual entries. If the current FY had no transactions, the prior FY is shown as a fallback (FY{financials.fy} above).
                  Uploaded documents are used only for the client's knowledge base and AI chat — tax planning uses the live Xero feed so figures always match the accounting system.
                </>
              ) : (
                <>
                  This client is <strong className="text-foreground">not connected to Xero</strong>, so no financial data is available. Action items shown are generic reminders only — specific dollar estimates require a Xero connection.
                  Connect Xero under Integrations → Xero to enable accurate year-end planning.
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-3 pb-3 text-center">
          <p className="text-2xl font-bold">{plan.totalItems || 0}</p>
          <p className="text-[10px] text-muted-foreground uppercase">Action items</p>
        </CardContent></Card>
        <Card className="border-red-500/30"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-2xl font-bold text-red-500">{plan.criticalItems || 0}</p>
          <p className="text-[10px] text-red-500 uppercase">Critical</p>
        </CardContent></Card>
        <Card className="border-amber-500/30"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-2xl font-bold text-amber-500">{(plan.items || []).filter((i: any) => i.priority === 'high').length}</p>
          <p className="text-[10px] text-amber-500 uppercase">High priority</p>
        </CardContent></Card>
        <Card className="border-green-500/30"><CardContent className="pt-3 pb-3 text-center">
          <p className="text-2xl font-bold text-green-500">{fmtCurrency(plan.totalEstimatedSaving || 0)}</p>
          <p className="text-[10px] text-green-500 uppercase">Potential saving</p>
        </CardContent></Card>
      </div>

      {/* Action Items with deep detail */}
      <div className="space-y-4">
        {(plan.items || []).map((item: any, i: number) => {
          const meta = CATEGORY_META[item.category] || DEFAULT_META;
          const Icon = meta.icon;
          const priorityColor = PRIORITY_COLORS[item.priority] || '#6b7280';
          const priorityStyle = PRIORITY_STYLES[item.priority] || 'bg-muted border-l-muted';

          return (
            <Card key={i} className={cn('border-l-4', priorityStyle)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge style={{ backgroundColor: priorityColor + '20', color: priorityColor, border: 'none' }} className="text-[9px] uppercase">{item.priority}</Badge>
                        <Badge variant="outline" className="text-[9px]">{item.category}</Badge>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="h-3 w-3" />{item.deadline}</span>
                      </div>
                      <CardTitle className="text-base">{item.action}</CardTitle>
                    </div>
                  </div>
                  {item.estimatedSaving > 0 && (
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-green-600">{fmtCurrency(item.estimatedSaving)}</p>
                      <p className="text-[10px] text-muted-foreground">est. saving</p>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>

                {/* Step-by-step */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-3.5 w-3.5 text-primary" />
                    <p className="text-xs font-semibold uppercase tracking-wider">How to action</p>
                  </div>
                  <ol className="space-y-1.5 pl-1">
                    {meta.howTo.map((step, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs leading-relaxed">
                        <span className="h-4 w-4 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{j + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Documents */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-3.5 w-3.5 text-primary" />
                      <p className="text-xs font-semibold uppercase tracking-wider">Documents to gather</p>
                    </div>
                    <ul className="space-y-1">
                      {meta.docsToGather.map((doc, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs leading-relaxed">
                          <CheckCircle2 className="h-3 w-3 text-green-600 mt-0.5 shrink-0" />
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="h-3.5 w-3.5 text-primary" />
                      <p className="text-xs font-semibold uppercase tracking-wider">ATO references</p>
                    </div>
                    <ul className="space-y-1">
                      {meta.atoRefs.map((ref, j) => (
                        <li key={j}>
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-1.5 text-xs leading-relaxed text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>{ref.title}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Internal tools */}
                {meta.internalTools && meta.internalTools.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="h-3.5 w-3.5 text-primary" />
                      <p className="text-xs font-semibold uppercase tracking-wider">Run in RoniDoc</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {meta.internalTools.map(tool => (
                        <button
                          key={tool.href}
                          onClick={() => navigate(tool.href)}
                          className="px-3 py-1.5 text-xs rounded-md border hover:bg-muted/60 transition-colors"
                        >
                          {tool.title} →
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline ATO rule */}
                <div className="p-2 rounded bg-muted/40 border text-[11px] text-muted-foreground italic leading-relaxed">
                  <strong className="not-italic text-foreground">ATO rule:</strong> {item.atoRule}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="p-3 rounded-md bg-amber-500/5 border border-amber-500/20 text-[11px] text-muted-foreground leading-relaxed">
        <strong className="text-amber-600">Important:</strong> Estimated savings are indicative only, based on simplified assumptions about marginal tax rates and eligibility.
        Every situation has specific facts — always confirm eligibility and dollar impact before actioning.
        ATO rulings and thresholds change year to year; verify current-year values using the linked ATO pages.
        This tool does not constitute tax advice.
      </div>
    </div>
  );
}
